/**
 * Is "Duo" multiplayer offered in THIS runtime?
 *
 * ── OWNER'S DECISION (2026-08-03): Duo IS RETIRED from the product ──────────────────────
 *
 * On ALL platforms — web, PWA, APK and MSI. It is not a per-environment gate: it is a
 * product retirement. The switch below (`isDuoFeatureEnabled`) is **off by
 * default** and nothing in the user surface turns it on: neither the module registry, nor
 * the home, nor the collection, nor the `/games/duo` route (which is not even registered).
 *
 * The reason, in one line: **no store listing should promise a feature that the
 * binary cannot run.** The Duo client requests `/api/p2p` on the SAME origin; in
 * the native binary the origin is `tauri://localhost`, where there is no server and there cannot
 * be one. Making it work requires deploying `apps/signaling`, and the owner has preferred
 * to keep Melodimals' zero infrastructure cost.
 *
 * ── The implementation is NOT deleted ───────────────────────────────────────────────────────
 *
 * `packages/game-core/src/p2p`, `apps/web/src/lib/p2p` and `apps/signaling` remain alive,
 * compiling and with their unit tests green. What is retired is the ENTRY POINT.
 * How to turn it back on —in this app or in any other in the portfolio— is written in
 * `docs/P2P_SIGNALING_ENABLEMENT.md`. The record of the decision and its numbers, in
 * `docs/store/DUO_DECISION.md`.
 *
 * ── The two conditions, deliberately separated ──────────────────────────────────────────
 *
 *   1. `isDuoFeatureEnabled()` — the PRODUCT SWITCH: do we want to offer this?
 *      Off by default everywhere. This is what was retired.
 *   2. `isSignalingReachable()` — the ENVIRONMENT CAPABILITY: is there reachable signaling?
 *      It is still true on the web (same origin) and false on native without an injected URL.
 *
 * `isDuoEnabled()` = both. They are kept separate because they answer different questions:
 * turning the switch on without deploying signaling would leave Duo visible and broken on
 * the APK, which is exactly the failure we came from.
 */

import { isTauriRuntime } from "@melodimals/monetization";

declare global {
  interface Window {
    /** Config baked by environment (`config.js` of `pages-deploy.sh`) — optional. */
    __APP_CONFIG__?: { p2pSignalingUrl?: string; duoEnabled?: boolean | string };
  }
}

/** What counts as "yes" in a Vite environment variable (always arrives as a string). */
function isTruthyFlag(value: unknown): boolean {
  return value === true || value === "true" || value === "1";
}

/**
 * Duo PRODUCT SWITCH. **Off unless someone turns it on on purpose.**
 *
 * Two sources, in this order:
 *
 *   1. `window.__APP_CONFIG__.duoEnabled` — config.js baked by environment (Pages). It is
 *      changed without rebuilding.
 *   2. `VITE_FEATURE_DUO` — baked into the bundle. It is the only path in the native binary,
 *      which loads no config.js: there the bundle IS the environment.
 *
 * Absent, empty or anything else = off. See `docs/P2P_SIGNALING_ENABLEMENT.md`.
 */
export function isDuoFeatureEnabled(): boolean {
  const runtime = globalThis.window?.__APP_CONFIG__?.duoEnabled;
  if (runtime !== undefined) {
    return isTruthyFlag(runtime);
  }
  return isTruthyFlag(import.meta.env.VITE_FEATURE_DUO);
}

/**
 * Signaling URL injected by configuration, if any. Same two sources as the
 * switch: `window.__APP_CONFIG__.p2pSignalingUrl` and `VITE_P2P_SIGNALING_URL`.
 *
 * Empty = not configured.
 */
export function configuredSignalingUrl(): string | undefined {
  const runtime = globalThis.window?.__APP_CONFIG__?.p2pSignalingUrl;
  const baked: unknown = import.meta.env.VITE_P2P_SIGNALING_URL;
  const url = runtime || (typeof baked === "string" ? baked : undefined);
  return url === "" ? undefined : url;
}

/** Base the signaling calls would talk to if Duo were on. */
export function signalingBaseUrl(): string {
  return configuredSignalingUrl() ?? "/api/p2p";
}

/**
 * ENVIRONMENT CAPABILITY: is there signaling that Duo can reach from here?
 *
 * Web/PWA: yes, the same origin resolves `/api/p2p`. Native: only if the build injected a
 * URL. Note: this does NOT decide whether Duo is offered — that is `isDuoEnabled()`.
 */
export function isSignalingReachable(): boolean {
  return configuredSignalingUrl() !== undefined || !isTauriRuntime();
}

/**
 * Is Duo offered? Product switch **AND** environment capability.
 *
 * Today it returns `false` everywhere because the switch is off. It is the only
 * predicate the user surface is allowed to query.
 */
export function isDuoEnabled(): boolean {
  return isDuoFeatureEnabled() && isSignalingReachable();
}
